import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { History, User, ChevronDown, ChevronRight } from "lucide-react";

const STAGE_STYLE = {
  new: "bg-slate-100 text-slate-600", quoting: "bg-[#E6ECF5] text-[#1B4383]",
  quoted: "bg-amber-100 text-amber-700", won: "bg-emerald-100 text-emerald-700",
  lost: "bg-rose-100 text-rose-700",
};
const ACTION_DOT = {
  created: "bg-emerald-500", updated: "bg-slate-400", stage_changed: "bg-[#1B4383]",
  items_added: "bg-sky-500", awarded: "bg-amber-500", quote_saved: "bg-slate-500",
  quote_emailed: "bg-violet-500", revision_created: "bg-teal-500",
};
const rel = (iso) => {
  try {
    const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch { return iso; }
};

const PREVIEW = 6;

export const ActivityLog = ({ entityType, entityId, url, refreshKey = 0, showStage = false }) => {
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api.get(url || `/activity/${entityType}/${entityId}`).then((r) => setItems(r.data)).catch(() => {});
  }, [entityType, entityId, url]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const visible = showAll ? items : items.slice(0, PREVIEW);
  const hasMore = items.length > PREVIEW;

  return (
    <div className="rounded-lg border border-border bg-white p-6" data-testid="activity-log">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center justify-between gap-2"
        data-testid="activity-log-toggle"
      >
        <span className="flex items-center gap-2">
          <History className="h-4 w-4 text-[#1B4383]" />
          <h3 className="font-heading text-sm font-bold tracking-tight">Activity Log</h3>
          {items.length > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500" data-testid="activity-count">{items.length}</span>
          )}
        </span>
        {collapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {!collapsed && (
        !items.length ? (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="activity-empty">No activity recorded yet.</p>
        ) : (
          <>
            <ol className="relative mt-4 space-y-4 border-l border-border pl-5">
              {visible.map((a) => (
                <li key={a.id} className="relative" data-testid={`activity-${a.id}`}>
                  <span className={`absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-white ${ACTION_DOT[a.action] || "bg-slate-400"}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-800">{a.summary}</span>
                    {showStage && a.stage && (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STAGE_STYLE[a.stage] || "bg-slate-100 text-slate-600"}`}>{a.stage}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />{a.actor_name} · {rel(a.at)}
                  </div>
                </li>
              ))}
            </ol>
            {hasMore && (
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="mt-4 text-xs font-medium text-[#1B4383] hover:underline"
                data-testid="activity-show-more"
              >
                {showAll ? "Show less" : `Show all ${items.length}`}
              </button>
            )}
          </>
        )
      )}
    </div>
  );
};

export default ActivityLog;
